import type { Note } from "@engram/core";
import { FileText, X } from "lucide-react";
import { useEffect } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useRow } from "../db/client";

/*
 * Note tabs (Evernote desktop): several notes open side by side above the editor. The active tab always
 * shows the open note, so clicking a note in the list replaces the active tab, like Evernote; "Open in
 * new tab" (note menu, middle-click a card) adds one. The strip appears only with two or more tabs.
 * Per device; desktop layout only.
 */

const MAX_TABS = 12;

interface TabState {
  tabs: string[];
  active: number;
  /** The open note changed (list click, link, search): it fills the active tab, or switches to its tab. */
  follow: (id: string) => void;
  open: (id: string) => void;
  /** By note id, not position: two tabs whose notes vanish together must each close their own tab. */
  close: (id: string) => string | null;
  activate: (index: number) => void;
}

export const useTabs = create<TabState>()(
  persist(
    (set, get) => ({
      tabs: [],
      active: 0,
      follow: (id) => {
        const { tabs, active } = get();
        const at = tabs.indexOf(id);
        if (at >= 0) return void (at !== active && set({ active: at }));
        if (!tabs.length) return set({ tabs: [id], active: 0 });
        const next = [...tabs];
        next[Math.min(active, next.length - 1)] = id;
        set({ tabs: next, active: Math.min(active, next.length - 1) });
      },
      open: (id) => {
        const { tabs } = get();
        const at = tabs.indexOf(id);
        if (at >= 0) return set({ active: at });
        const next = [...tabs, id].slice(-MAX_TABS);
        set({ tabs: next, active: next.length - 1 });
      },
      close: (id) => {
        const { tabs, active } = get();
        const index = tabs.indexOf(id);
        if (index < 0) return null;
        const next = tabs.filter((_, i) => i !== index);
        const nextActive = index < active ? active - 1 : index === active ? Math.min(active, next.length - 1) : active;
        set({ tabs: next, active: Math.max(0, nextActive) });
        // The note to show now, when the open tab was closed.
        return index === active ? (next[Math.max(0, nextActive)] ?? null) : null;
      },
      activate: (index) => set({ active: index }),
    }),
    { name: "engram.tabs" },
  ),
);

/** Asks the notes pane to open a note in a new tab (note menu, list context menu, middle-click). */
export function openInTab(id: string) {
  window.dispatchEvent(new CustomEvent("engram:open-tab", { detail: { id } }));
}

export function TabStrip({ selected, onSelect }: { selected: string | null; onSelect: (id: string | null) => void }) {
  const { tabs, active, follow, open, close, activate } = useTabs();
  useEffect(() => {
    if (selected) follow(selected);
  }, [selected, follow]);
  useEffect(() => {
    const onOpen = (e: Event) => {
      const { id } = (e as CustomEvent<{ id: string }>).detail;
      open(id);
      onSelect(id);
    };
    window.addEventListener("engram:open-tab", onOpen);
    return () => window.removeEventListener("engram:open-tab", onOpen);
  }, [open, onSelect]);
  if (tabs.length < 2) return null;
  const shut = (id: string) => {
    const show = close(id);
    if (show) onSelect(show);
  };
  return (
    <div className="tab-strip" role="tablist" aria-label="Open notes">
      {tabs.map((id, i) => (
        <Tab
          key={id}
          id={id}
          active={i === active}
          onClick={() => {
            activate(i);
            onSelect(id);
          }}
          onClose={() => shut(id)}
        />
      ))}
    </div>
  );
}

function Tab({ id, active, onClick, onClose }: { id: string; active: boolean; onClick: () => void; onClose: () => void }) {
  const note = useRow<Pick<Note, "title" | "deleted" | "trashed_at">>("SELECT title, deleted, trashed_at FROM notes WHERE id = ?", [id], ["notes"]);
  const gone = note === null || note?.deleted;
  useEffect(() => {
    if (gone) onClose(); // deleted elsewhere
  }, [gone]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div
      role="tab"
      aria-selected={active}
      className={`note-tab${active ? " active" : ""}`}
      title={note?.title || "Untitled"}
      onMouseDown={(e) => {
        if (e.button === 1) {
          e.preventDefault();
          onClose();
        }
      }}
      onClick={onClick}
    >
      <FileText size={13} className="note-tab-icon" />
      <span className="note-tab-title">{note?.title || "Untitled"}</span>
      <button
        className="note-tab-x"
        aria-label="Close tab"
        title="Close tab"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <X size={12} />
      </button>
    </div>
  );
}
